/**
 * Developed by Spell Solutions for Trivinci Systems, LLC.
 *
 * Developer attribution only. Ownership, licensing, and permitted use are
 * governed by the applicable agreement and applicable law.
 */

/**
 * Reverse-geocodes a GPS fix into a human "City, Country" label for the HUD,
 * using BigDataCloud's free client-side reverse-geocode API (no key, CORS-
 * enabled, meant for browser use). Chosen over OpenStreetMap Nominatim because
 * it returns the postal CITY even for unincorporated areas — matching what the
 * iOS app gets from Apple's CLGeocoder (e.g. "Fulshear" where Nominatim only
 * has the county). One instance per HUD so each car has its own cache/throttle.
 *
 * Throttled hard to respect Nominatim's usage policy: at most one lookup per
 * ~20 s, and only when the car has moved > ~120 m since the last lookup. A
 * stationary car resolves once and stays put. `label()` returns the cached
 * name synchronously (null until the first lookup resolves) and kicks off the
 * async fetch in the background.
 */
/** Human-readable coordinates, for when a place name cannot be resolved.
 *
 *  The HUD used to sit on "Locating…" indefinitely whenever the reverse
 *  geocode failed, which reads as "no GPS lock" even though the fix is good
 *  and the map is drawing from it. The lookup calls a third-party host, so it
 *  can be blocked by browser tracking protection (Edge blocks it) or simply be
 *  unreachable — none of which says anything about the GPS. Four decimals is
 *  about 11 m, plenty to confirm a fix is real. */
export function formatCoords(lat: number, lon: number): string {
  const ns = lat >= 0 ? "N" : "S";
  const ew = lon >= 0 ? "E" : "W";
  return `${Math.abs(lat).toFixed(4)}\u00B0${ns}, ${Math.abs(lon).toFixed(4)}\u00B0${ew}`;
}

export class ReverseGeocoder {
  private lat: number | null = null;
  private lon: number | null = null;
  private at = 0;
  private name: string | null = null;
  private inFlight = false;

  /** @param onUpdate called when a newly-resolved label differs from the
   *  current one (e.g. to trigger a re-render in a reactive component). */
  constructor(private onUpdate?: () => void) {}

  label(lat: number | null | undefined, lon: number | null | undefined): string | null {
    if (lat == null || lon == null) return null;
    const now = Date.now();
    const moved = this.lat == null ? Infinity : distMeters(this.lat, this.lon!, lat, lon);
    if (!this.inFlight && (this.name == null || moved > 120 || now - this.at > 20_000)) {
      this.inFlight = true;
      this.lat = lat;
      this.lon = lon;
      this.at = now;
      void this.lookup(lat, lon);
    }
    return this.name;
  }

  private async lookup(lat: number, lon: number): Promise<void> {
    try {
      const url =
        `https://api.bigdatacloud.net/data/reverse-geocode-client` +
        `?latitude=${lat}&longitude=${lon}&localityLanguage=en`;
      const res = await fetch(url, { headers: { Accept: "application/json" } });
      if (!res.ok) return;
      const d = (await res.json()) as {
        locality?: string;
        city?: string;
        principalSubdivision?: string;
        countryName?: string;
      };
      // Prefer the locality (postal city, e.g. "Fulshear"), then the broader
      // city field, then the state as a last resort.
      const place = d.locality || d.city || d.principalSubdivision;
      let country = (d.countryName || "").replace(/\s*\(the\)\s*$/i, "");
      if (country === "United States of America") country = "United States";
      const label = [place, country].filter(Boolean).join(", ");
      if (label && label !== this.name) {
        this.name = label;
        this.onUpdate?.();
      }
    } catch {
      /* transient — keep the previous name, retry on the next throttle window */
    } finally {
      this.inFlight = false;
    }
  }
}

function distMeters(la1: number, lo1: number, la2: number, lo2: number): number {
  const R = 6_371_000;
  const toR = Math.PI / 180;
  const dLa = (la2 - la1) * toR;
  const dLo = (lo2 - lo1) * toR;
  const s =
    Math.sin(dLa / 2) ** 2 +
    Math.cos(la1 * toR) * Math.cos(la2 * toR) * Math.sin(dLo / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(s));
}
